/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/iframe-has-title */
import { useState, useEffect } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { ICONS } from "../constants/icons";
import { useUser } from "../contexts/UserContext";
import { ROUTES } from "../App";
import { get, off, onValue, ref } from "firebase/database";
import { database } from "../constants/firebase";
import useMoveTo from "../constants/navigation";
import MobileHeader from "../components/MobileHeader";
import BunnyVideoEmbed from "../components/BunnyVideoEmbed";
import { checkVideoStatus, linkifyText } from "../utils/utils";
import ViewProfileFriends from "../components/ViewProfileFriends";
import ViewProfileTotalMembers from "../components/ViewProfileTotalMembers";
import Lottie from "lottie-react";
import fireAnimation from "../assets/animations/fire.json";

const Profile = () => {
    const navigate = useMoveTo();
    const { currentUser, loading } = useUser();
    const [totalMembers, setTotalMembers] = useState(0);
    const [streakDays, setStreakDays] = useState(0);
    const [loadingMembers, setLoadingMembers] = useState(true);
    const [videoThumbnail, setVideoThumbnail] = useState("");
    const [imageLoading, setImageLoading] = useState(true);
    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const [videoStatus, setVideoStatus] = useState(null);
    const [friendCount, setFriendCount] = useState(0);
    const [loadingFriends, setLoadingFriends] = useState(true);
    const [viewFriends, setViewFriends] = useState(false);
    const [viewTotalMembers, setViewTotalMembers] = useState(false);


    // Generate video thumbnail when video URL changes
    useEffect(() => {
        if (currentUser?.video) {
            checkVideoStatus(currentUser.video).then((status) => {
                setVideoStatus(status);
            });
        }
    }, [currentUser?.video]);

    useEffect(() => {

        const getVideoThumbnail = (videoUrl) => {
            return new Promise((resolve) => {
                const video = document.createElement('video');
                video.src = videoUrl;
                video.crossOrigin = 'anonymous';

                video.addEventListener('loadedmetadata', () => {
                    video.currentTime = 0.5; // Capture at 0.5 seconds
                });

                video.addEventListener('seeked', () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    resolve(canvas.toDataURL('image/jpeg'));
                });

                video.addEventListener('error', () => {
                    resolve(IMAGES.statues); // Fallback image
                });
            });
        };

        if (currentUser?.video) {
            getVideoThumbnail(currentUser.video).then((thumbnail) => {
                setVideoThumbnail(thumbnail);
            });
        }
    }, [currentUser?.video]);

    // Calculate total members across all hosted communities
    useEffect(() => {
        if (!currentUser?.uid) return;

        const communitiesRef = ref(database, "Communities");

        const unsubscribe = onValue(communitiesRef, (snapshot) => {
            const data = snapshot.val();
            if (!data) {
                setTotalMembers(0);
                setStreakDays(0);
                setLoadingMembers(false);
                return;
            }

            const uniqueUsers = new Set();
            let maxStreak = 0; // 👈 track biggest streak

            for (const communityId in data) {
                const community = data[communityId];

                // 1️⃣ Only communities hosted by THIS user
                if (community.hostId !== currentUser.uid) continue;

                // 2️⃣ Collect member IDs
                if (community.members) {
                    for (const memberId in community.members) {
                        uniqueUsers.add(memberId);
                    }
                }

                // 3️⃣ Get biggest streakDays
                const streak = Number(community.streakCount) || 0;
                if (streak > maxStreak) {
                    maxStreak = streak;
                }
            }

            // 3️⃣ (Optional) remove host if present
            uniqueUsers.delete(currentUser.uid);

            // 4️⃣ FINAL COUNT
            setTotalMembers(uniqueUsers.size);
            setStreakDays(maxStreak); // 👈 biggest streak
            setLoadingMembers(false);
        });

        return () => off(communitiesRef);
    }, [currentUser?.uid]);


    useEffect(() => {
        if (!currentUser?.uid) return;

        const fetchFriendCount = async () => {
            try {
                const friendsSnap = await get(ref(database, `Users/${currentUser.uid}/friends`));
                if (friendsSnap.exists()) {
                    const friendsData = friendsSnap.val();
                    setFriendCount(Object.keys(friendsData).length);
                } else {
                    setFriendCount(0);
                }
            } catch (err) {
                console.error("Error fetching friends:", err);
                setFriendCount(0);
            } finally {
                setLoadingFriends(false);
            }
        };

        fetchFriendCount();
    }, [currentUser?.uid]);

    return (
        <div className="flex flex-col h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-hidden">
            {/* Mobile Header */}
            <MobileHeader title={"Profile"} />

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar */}
                <Sidebar />

                {/* Main Content */}
                <main className="flex-1 overflow-y-auto bg-white text-black dark:bg-[#1A1A1A] dark:text-white px-6 pt-5 pb-[88px] md:pb-20">
                    {/* Profile Header */}
                    <div className="border-b border-[#D9D9D9]/20 flex items-center justify-between pb-4">
                        <h2 className="text-2xl md:text-3xl font-semibold md:mb-0 hidden lg:block">Profile</h2>
                        <button
                            onClick={() => navigate(ROUTES.SETTINGS)}
                        >
                            <img
                                src={ICONS.gear}
                                className="w-8 h-8 cursor-pointer block lg:hidden brightness-0 dark:invert"
                                alt="Settings"

                            />
                        </button>
                        {/* <h2 className="text-2xl md:text-3xl font-semibold md:mb-0 block lg:hidden">Profile</h2> */}

                        <div className="flex items-center space-x-3">
                            <button
                                type="button"
                                className="btn-primary min-w-28 max-w-28 !rounded-2xl text-sm px-4 py-2"
                                onClick={() => navigate(ROUTES.EDIT_PROFILE)}
                            >
                                Edit
                            </button>


                            <img
                                src={ICONS.gear}
                                className="w-8 h-8 cursor-pointer hidden lg:block brightness-0 dark:invert"
                                alt="Settings"
                                onClick={() => navigate(ROUTES.SETTINGS)}
                            />
                        </div>
                    </div>

                    {/* Profile Card */}
                    {/* Profile Card */}
                    <div className="mt-6 flex flex-col md:flex-row md:items-center gap-4">

                        {/* Left Side: Image + User Info */}
                        <div className="flex flex-wrap items-center gap-4 flex-1 min-w-0">

                            {/* Profile Image */}
                            <div className="flex-shrink-0 flex flex-col items-center">

                                <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden">
                                    {imageLoading && (
                                        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 rounded-full" />
                                    )}
                                    <img
                                        src={
                                            currentUser?.profilePic ||
                                            'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef'
                                        }
                                        alt="Profile"
                                        className={`w-full h-full object-cover rounded-full transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'
                                            }`}
                                        onLoad={() => setImageLoading(false)}
                                        onError={() => setImageLoading(false)}
                                    />
                                </div>

                                {currentUser.role === "Host" && streakDays > 0 && (
                                    <div className="mt-1 flex items-center gap-1 bg-white rounded-full px-1.5 py-0.5 shadow">
                                        <Lottie
                                            animationData={fireAnimation}
                                            loop
                                            autoplay
                                            className="w-4 h-4"
                                        />
                                        <span className="text-[10px] font-bold text-black">
                                            {streakDays}
                                        </span>
                                    </div>
                                )}

                            </div>

                            {/* User Info */}
                            <div className="flex-1 min-w-0 space-y-1 overflow-hidden">

                                <h3
                                    className={`flex items-center gap-2 text-xl md:text-2xl font-bold ${currentUser?.role === "Host"
                                        ? "dark:glow-text-dark glow-text-light"
                                        : ""
                                        }`}
                                >
                                    <span className="block max-w-[90%] truncate">
                                        {!loading ? currentUser?.name : "User"}
                                    </span>

                                    {currentUser?.isTeamUser && (
                                        <svg
                                            width="24px"
                                            height="24px"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            stroke="#327691"
                                        >
                                            <path
                                                d="M10.5213 2.62368C11.3147 1.75255 12.6853 1.75255 13.4787 2.62368L14.4989 3.74391C14.8998 4.18418 15.4761 4.42288 16.071 4.39508L17.5845 4.32435C18.7614 4.26934 19.7307 5.23857 19.6757 6.41554L19.6049 7.92905C19.5771 8.52388 19.8158 9.10016 20.2561 9.50111L21.3763 10.5213C22.2475 11.3147 22.2475 12.6853 21.3763 13.4787L20.2561 14.4989C19.8158 14.8998 19.5771 15.4761 19.6049 16.071L19.6757 17.5845C19.7307 18.7614 18.7614 19.7307 17.5845 19.6757L16.071 19.6049C15.4761 19.5771 14.8998 19.8158 14.4989 20.2561L13.4787 21.3763C12.6853 22.2475 11.3147 22.2475 10.5213 21.3763L9.50111 20.2561C9.10016 19.8158 8.52388 19.5771 7.92905 19.6049L6.41553 19.6757C5.23857 19.7307 4.26934 18.7614 4.32435 17.5845L4.39508 16.071C4.42288 15.4761 4.18418 14.8998 3.74391 14.4989L2.62368 13.4787C1.75255 12.6853 1.75255 11.3147 2.62368 10.5213L3.74391 9.50111C4.18418 9.10016 4.42288 8.52388 4.39508 7.92905L4.32435 6.41553C4.26934 5.23857 5.23857 4.26934 6.41554 4.32435L7.92905 4.39508C8.52388 4.42288 9.10016 4.18418 9.50111 3.74391L10.5213 2.62368Z"
                                                strokeWidth="1.5"
                                            />
                                            <path
                                                d="M9 12L11 14L15 10"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    )}
                                </h3>

                                <p className="text-sm md:text-base text-primary truncate">
                                    {currentUser.niche}
                                </p>

                                {/* Stats */}
                                <div className="flex items-center space-x-4 text-sm md:text-base">
                                    {currentUser?.role === "Host" && (
                                        <button onClick={() => setViewTotalMembers(true)}>
                                            <div className="flex items-center space-x-2">
                                                <strong>{loadingMembers ? '...' : totalMembers}</strong>
                                                <span className="dark:text-white text-black">Total members</span>
                                            </div>
                                        </button>
                                    )}

                                    <button
                                        className=""
                                        onClick={() => setViewFriends(true)}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <strong>{loadingFriends ? '...' : friendCount}</strong>
                                            <span className="dark:text-white text-black">Friends</span>
                                        </div>
                                    </button>
                                </div>
                                {/* Bio */}
                                {currentUser?.bio && (
                                    <p className="text-sm md:text-base text-black dark:text-[#CCCCCC] truncate">
                                        {linkifyText(currentUser.bio, false, "text-primary")}
                                    </p>
                                )}


                                {/* Website Link */}
                                {currentUser?.website && (
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();

                                            const website = currentUser.website.startsWith('http')
                                                ? currentUser.website
                                                : `https://${currentUser.website}`;

                                            // 👇 Send message to the React Native WebView
                                            if (window.ReactNativeWebView) {
                                                window.ReactNativeWebView.postMessage(
                                                    JSON.stringify({
                                                        type: 'OPEN_EXTERNAL_WEBSITE',
                                                        url: website,
                                                    })
                                                );
                                            } else {
                                                // Fallback for normal browsers
                                                window.open(website, '_blank', 'noopener,noreferrer');
                                            }
                                        }}
                                        className="block text-sm md:text-base text-primary truncate"
                                    >
                                        {currentUser.website}
                                    </a>
                                )}

                            </div>
                        </div>

                        {/* Activity Button */}
                        <div className="w-full md:w-auto flex flex-wrap gap-3">
                            <button
                                onClick={() => navigate(ROUTES.ACCOUNT_ACTIVITY)}
                                className="px-5 py-2.5 text-sm font-medium bg-transparent text-primary dark:text-white text-white border border-[#327691] hover:bg-[#327691] hover:text-white rounded-lg transition"
                            >
                                Activity
                            </button>
                            {currentUser?.isAdmin && (
                                <button
                                    onClick={() => navigate(ROUTES.ADMIN_ANALYTICS)}
                                    className="px-5 py-2.5 text-sm font-medium bg-[#327691] text-white rounded-lg transition shadow-md"
                                >
                                    Analytics
                                </button>
                            )}
                        </div>

                    </div>



                    {/* Portfolio Section */}
                    <div className="mt-4 border-t border-[#D9D9D9]/20 pt-4">
                        {/* <h4 className="text-xl md:text-2xl lg:text-3xl font-semibold text-primary mb-4 text-center">
                            Portfolio
                        </h4> */}

                        {/* 🎞️ Show video with shimmer */}
                        {currentUser?.video ? (
                            <div className="relative">
                                <BunnyVideoEmbed
                                    videoUrl={currentUser.video}
                                    orientation={currentUser.videoOrientation}
                                    videoWidth={currentUser.videoWidth}
                                    videoHeight={currentUser.videoHeight}
                                    variant="inline"
                                    isLoading={isIframeLoading}
                                    onLoad={() => setIsIframeLoading(false)}
                                    autoplay={true}
                                    muted={true}
                                    wrapperClassName="mx-auto shadow-xl"
                                    iframeClassName={isIframeLoading || videoStatus === 2 ? "opacity-0 transition-opacity duration-300" : "opacity-100 transition-opacity duration-300"}
                                    title={`${currentUser.name || "Profile"} showreel`}
                                />

                                {/* Shimmer placeholder */}
                                {videoStatus === 2 && (
                                    <div className="absolute inset-0 flex items-center justify-center animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 bg-opacity-50 z-10 px-4 rounded-xl">
                                        <p className="dark:text-white text-black text-lg font-bold">Video processing. Please check back later.</p>
                                    </div>
                                )}

                            </div>
                        ) : currentUser?.portfolioBanner ? (
                            // 🖼️ Portfolio image with shimmer
                            <div className="relative w-full h-64 md:h-80 rounded-xl overflow-hidden bg-gray-200 dark:bg-gray-800">
                                {imageLoading && (
                                    <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700" />
                                )}
                                <img
                                    src={currentUser.portfolioBanner}
                                    alt="Portfolio Banner"
                                    className={`w-full h-full object-cover rounded-xl transition-opacity duration-300 ${imageLoading ? "opacity-0" : "opacity-100"}`}
                                    onLoad={() => setImageLoading(false)}
                                    onError={() => setImageLoading(false)}
                                />
                            </div>
                        ) : (
                            <p className="text-center text-gray-500">No portfolio added yet</p>
                        )}
                    </div>


                    <div className="mt-10 flex flex-col md:flex-row gap-8">

                        {/* About Section */}
                        <div className="md:w-1/2">
                            <h4 className="text-lg md:text-xl font-semibold mb-4 text-primary">About</h4>
                            {currentUser && (
                                <p className="text-black dark:text-[#CCCCCC] text-sm md:text-base whitespace-pre-line">
                                    {currentUser.about || 'No about information provided yet.'}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* View Friends Modal */}
                    {viewFriends && (
                        <ViewProfileFriends
                            isOpen={viewFriends}
                            onClose={() => setViewFriends(false)}
                            userId={currentUser.uid}
                        />
                    )}

                    {/* View Total Members Modal */}
                    {viewTotalMembers && (
                        <ViewProfileTotalMembers
                            isOpen={viewTotalMembers}
                            onClose={() => setViewTotalMembers(false)}
                            userId={currentUser.uid}
                        />

                    )}

                </main>
            </div>
        </div>
    );
};

export default Profile;
